import React, { useEffect, useMemo, useState } from 'react'
import config from 'src/config'
import styled from 'styled-components'

const { year, month, day, hour, minute } = config.PLAY_END_TIME
const DEFAULT_TARGET_DATE = new Date(year, month - 1, day, hour, minute, 0, 0)

type MiniCountdownProps = {
  targetDate?: string | Date
}

const Wrapper = styled.div`
  color: #fff;
  text-align: center;
  margin-top: 32px;

  h6 {
    margin-bottom: 8px;
    font-size: 1.25rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  @media (min-width: 1279px) {
    h6 {
      font-size: 32px;
    }
  }
`

const TimerCard = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  gap: 12px;
  border-radius: 16px;
  background: linear-gradient(139deg, rgba(83, 196, 181, 0.9) 0%, rgba(44, 139, 131, 0.95) 100%);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  backdrop-filter: blur(16px);

  font-size: 2.5rem;
  font-weight: 700;
  line-height: 1;

  span {
    min-width: 30px;
  }

  .separator {
    color: #fff;
    font-weight: 600;
  }

  @media (min-width: 1280px) {
    padding: 25px 35px;
    font-size: 32px;
  }
`

function formatUnit(value: number | string) {
  const stringValue = typeof value === 'number' ? value.toString() : value
  return stringValue.padStart(2, '0')
}

export default function MiniCountdown({ targetDate = DEFAULT_TARGET_DATE }: MiniCountdownProps) {
  const targetTimestamp = useMemo(() => {
    const dateValue = targetDate instanceof Date ? targetDate : new Date(targetDate)
    const timestamp = dateValue.getTime()
    return Number.isNaN(timestamp) ? null : timestamp
  }, [targetDate])

  const [remainingMs, setRemainingMs] = useState(() => {
    if (targetTimestamp === null) {
      return 0
    }
    return Math.max(0, targetTimestamp - Date.now())
  })

  useEffect(() => {
    if (targetTimestamp === null) {
      setRemainingMs(0)
      return
    }

    const updateRemaining = () => {
      setRemainingMs(Math.max(0, targetTimestamp - Date.now()))
    }

    updateRemaining()
    const intervalId = window.setInterval(updateRemaining, 1000)
    return () => window.clearInterval(intervalId)
  }, [targetTimestamp])

  const totalSeconds = Math.floor(remainingMs / 1000)
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60

  const hoursDisplay = targetTimestamp === null ? '--' : formatUnit(hours)
  const minutesDisplay = targetTimestamp === null ? '--' : formatUnit(minutes)
  const secondsDisplay = targetTimestamp === null ? '--' : formatUnit(seconds)

  return (
    <Wrapper>
      <h6>Live Starts In</h6>
      <TimerCard>
        <span>{hoursDisplay}</span>
        <span className="separator">:</span>
        <span>{minutesDisplay}</span>
        <span className="separator">:</span>
        <span>{secondsDisplay}</span>
      </TimerCard>
    </Wrapper>
  )
}
